import React from "react";
import { getBezierPath, getBezierEdgeCenter } from "react-flow-renderer";
import { useNodeActions } from "../context/NodeActionsContext";

/** Radio del círculo visible y del área de clic (más grande e invisible). */
const BUTTON_R = 11;
const HIT_R = 17;

/**
 * Arista con botón de borrado (0422 QA-UX). Tres defectos corregidos:
 *
 *  1. La ✕ se calculaba en el punto medio de la RECTA entre origen y destino, pero la arista es
 *     una CURVA: en cuanto los nodos no estaban alineados, el botón flotaba lejos del trazo.
 *     Ahora va en el centro de la curva (`getBezierEdgeCenter`, el mismo cálculo de la librería).
 *
 *  2. React Flow pone `pointer-events: visibleStroke` a las aristas: solo el TRAZO recibe clics.
 *     En la ✕ eso era el borde de 1,5 px del círculo — hacían falta varios intentos. Ahora todo el
 *     botón (y un anillo invisible alrededor) recibe el clic.
 *
 *  3. El color y el grosor venían EN LÍNEA en cada arista (`defaultEdgeOptions.style`), y el
 *     estilo en línea gana al CSS: el resaltado de hover y de «seleccionada» nunca se veía. Ahora
 *     el trazo se pinta desde el CSS (flowEditorStyles) y aquí solo se conserva el resto del estilo.
 */
const ButtonEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  animated,
  selected,
}) => {
  const { onDeleteEdge } = useNodeActions();

  const geometria = { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition };
  const edgePath = getBezierPath(geometria);
  const [cx, cy] = getBezierEdgeCenter(geometria);

  // Fuera `stroke` y `strokeWidth`: los decide el CSS según reposo / hover / seleccionada.
  const { stroke, strokeWidth, ...restoEstilo } = style || {};

  const borrar = (e) => {
    e.stopPropagation();
    onDeleteEdge(id);
  };

  return (
    <>
      <path
        id={id}
        style={restoEstilo}
        className={`react-flow__edge-path${animated ? " animated" : ""}`}
        d={edgePath}
        markerEnd={markerEnd}
      />

      {/* Hitbox invisible más amplio: facilita pasar por encima y seleccionar la arista */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        style={{ cursor: "pointer", pointerEvents: "stroke" }}
      />

      {/* Botón de borrado en el centro de la curva */}
      <g
        className={`edge-delete-btn${selected ? " seleccionada" : ""}`}
        onClick={borrar}
        // Sin esto, el mismo clic arrancaba el arrastre del lienzo o la selección de la arista.
        onMouseDown={(e) => e.stopPropagation()}
        style={{ cursor: "pointer", pointerEvents: "all" }}
      >
        <title>Eliminar conexión (o selecciónala y pulsa Supr)</title>
        {/* Área de clic invisible, algo mayor que el círculo */}
        <circle cx={cx} cy={cy} r={HIT_R} fill="transparent" />
        <circle className="edge-delete-sombra" cx={cx} cy={cy} r={BUTTON_R + 1} fill="rgba(0,0,0,0.18)" style={{ pointerEvents: "none" }} />
        <circle className="edge-delete-fondo" cx={cx} cy={cy} r={BUTTON_R} strokeWidth={1.5} />
        <text
          className="edge-delete-x"
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={15}
          fontWeight={700}
          style={{ userSelect: "none", pointerEvents: "none", fontFamily: "sans-serif" }}
        >
          ×
        </text>
      </g>
    </>
  );
};

export default ButtonEdge;
